import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { auth, db } from '../firebase.js';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';

// Create the context
const UserContext = createContext(null);

// Custom hook to use the user context
export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

// Provider component
export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [hasAccess, setHasAccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [justAuthenticated, setJustAuthenticated] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Function to handle logout
  const handleLogout = useCallback(async () => {
    try {
      await auth.signOut();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  }, []);
  
  // Function to handle sign up
  const handleSignUp = useCallback((newUser) => {
    setUser(newUser);
    setJustAuthenticated(true);
  }, []);
  
  // Function to handle login
  const handleLogin = useCallback((user) => {
    setUser(user);
    setJustAuthenticated(true);
  }, []);
  
  // Handle authentication state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setLoading(true);
      
      if (currentUser) {
        try {
          // Get user data and check access
          const userRef = doc(db, 'users', currentUser.uid);
          let userDoc = await getDoc(userRef);
          
          // Create user document if it doesn't exist
          if (!userDoc.exists()) {
            let defaultAccess = false;
            if (currentUser.email === 'mlarkins@gmail.com' || currentUser.uid === 'your-test-uid') {
              defaultAccess = true;
            }
            await setDoc(userRef, {
              email: currentUser.email,
              displayName: currentUser.displayName || 'N/A',
              hasAccess: defaultAccess,
              createdAt: new Date().toISOString()
            });
            userDoc = await getDoc(userRef);
          }
          
          const userData = userDoc.data();
          const accessGranted = userData.hasAccess === true;
          
          setUser(currentUser);
          setHasAccess(accessGranted);
          
          if (!accessGranted) {
            navigate('/pending-access');
          } else if (location.pathname === '/login' || location.pathname === '/signup' || location.pathname === '/pending-access') {
            navigate('/');
          }
        } catch (error) {
          console.error('Error during authentication process:', error);
          setUser(currentUser);
          setHasAccess(false);
        }
      } else {
        // No user, clear everything
        setUser(null);
        setHasAccess(false);
        
        // Redirect to login if not already there
        if (location.pathname !== '/login' && location.pathname !== '/signup') {
          navigate('/login');
        }
      }
      
      setLoading(false);
    });
    
    return () => {
      unsubscribe();
    };
  }, [navigate, location.pathname]);
  
  // Reset justAuthenticated flag
  useEffect(() => {
    if (justAuthenticated) {
      const timer = setTimeout(() => {
        setJustAuthenticated(false);
      }, 2000);
      
      return () => {
        clearTimeout(timer);
      };
    }
  }, [justAuthenticated]);
  
  // Create the context value object
  const userContextValue = {
    user,
    hasAccess,
    loading,
    justAuthenticated,
    handleLogin,
    handleSignUp,
    handleLogout
  };
  
  // Provide the context value to children
  return (
    <UserContext.Provider value={userContextValue}>
      {children}
    </UserContext.Provider>
  );
}; 